import { useState } from "react";
import { theme } from "../theme.ts";

/**
 * Per-category caveats for the Plan-style Demand table.
 *
 * These are the things that make the dashboard and the planning workbook
 * disagree on specific rows. All were established by reconciling against
 * 2027_Plan_Final_v3.xlsx, and every one is a REPORTING difference — the
 * app reports raw NetSuite consumption; the workbook applies deductions
 * and renames on top. Recording them here so nobody re-derives them.
 */

function Note({
  title,
  tone = "amber",
  defaultOpen,
  children,
}: {
  title: string;
  tone?: "amber" | "blue" | "green";
  /** Starred notes are the load-bearing ones and open by default. */
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? title.startsWith("\u2605"));
  const c =
    tone === "amber"
      ? { bg: theme.color.warningFaint, border: theme.color.warningBorder, fg: theme.color.warningText }
      : tone === "green"
        ? { bg: theme.color.successFaint, border: theme.color.successBorder, fg: theme.color.successText }
        : { bg: theme.color.brandFaint, border: theme.color.brandBorder, fg: theme.color.brandInk };
  return (
    <div
      style={{
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: theme.radius.lg,
        fontSize: 12.5,
        lineHeight: 1.6,
        color: c.fg,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          all: "unset",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          padding: "10px 14px",
          cursor: "pointer",
          fontWeight: 700,
          color: c.fg,
        }}
      >
        <span
          aria-hidden
          style={{
            display: "inline-block",
            transition: "transform 120ms",
            transform: open ? "rotate(90deg)" : "none",
            opacity: 0.7,
            fontSize: 10,
          }}
        >
          &#9654;
        </span>
        {title}
      </button>
      {open && <div style={{ padding: "0 14px 10px 32px" }}>{children}</div>}
    </div>
  );
}

export function CategoryNotes({
  category,
}: {
  category: "Frame" | "Olefin" | "Pacifica" | "Sunbrella" | "Base" | "Trim" | "ProtectiveCover";
}) {
  return (
    <div style={{ display: "grid", gap: theme.space.sm }}>
      <Note tone="blue" defaultOpen title="These are ACTUALS — the plan file adjusts them">
        Every figure here is demand invoiced in NetSuite (Direct + L1 BOM +
        L2), bucketed Jul&nbsp;→&nbsp;Jun. The planning workbook starts from
        the same consumption numbers and then applies manual deductions, so
        specific rows are <i>expected</i> to differ. The notes below say
        which, and why.
      </Note>

      <Note
        tone="blue"
        defaultOpen
        title="★ These are units USED, not units sold — why NetSuite's Sales by Item reads lower"
      >
        <p style={{ margin: "0 0 6px" }}>
          Each figure counts every unit of the SKU that left the building — most
          of it inside finished umbrellas — because that is what has to be
          bought. NetSuite&rsquo;s <b>Sales by Item</b> report counts only units
          sold <i>on their own</i>, which for a component is a tiny fraction.
          The small grey number under each total is that sold-alone figure, so
          the two can be compared directly.
        </p>
        <p style={{ margin: 0, fontFamily: "monospace", fontSize: 12 }}>
          ALTO908-002, FY2026 &nbsp;— 2,911 used
          <br />
          &nbsp;&nbsp;&nbsp;&nbsp;14 sold on their own &nbsp;&nbsp;← all Sales by Item shows
          <br />
          &nbsp;1,534 itemised at $0 on orders — mostly alongside an umbrella
          <br />
          &nbsp;1,363 consumed through umbrella bills of materials
        </p>
      </Note>

      <Note title="Locations counted: Jurupa, March Products Texas and Type B — these are exactly March Products">
        <p style={{ margin: "0 0 6px" }}>
          Every figure on every tab is scoped to three locations:{" "}
          <b>15 Jurupa CA</b>, <b>64 March Products Texas</b> and{" "}
          <b>22 Type&nbsp;B</b> (bulk / Amazon fulfilment).
        </p>
        <p style={{ margin: "0 0 6px" }}>
          Those three are exactly NetSuite subsidiary&nbsp;1, March Products.{" "}
          <b>Location 65, Gold Crest Texas, is excluded</b> because it belongs
          to subsidiary&nbsp;4, <b>Gold Crest LLC</b> — a separate legal
          entity, not a March warehouse, carrying 63,834 invoiced units in
          FY2026. Its sales are not March demand. Locations 18, 40 and 43
          carry no invoiced volume.
        </p>
        <p style={{ margin: 0 }}>
          <b>The workbook itself is not consistent about this between tabs.</b>{" "}
          Its Base tab includes Type&nbsp;B — CFMT160-BLACK only reconciles as
          3,577 + 264 + 229 = 4,070 once it is counted — while its ATA&nbsp;FG
          note quotes 28,186 for ATA908-117, which is location&nbsp;15 alone
          (Type&nbsp;B adds the other 489). So a row that differs by a percent
          or two may simply be a scope difference on the plan side rather than
          a real one. Worth settling with whoever maintains the workbook.
        </p>
      </Note>

      {category === "Frame" && (
        <>
          <Note title="★ ATA908-117 reads ~78% higher than the plan — by design">
            <p style={{ margin: "4px 0" }}>
              The workbook's own ATA&nbsp;FG note records it:{" "}
              <i>
                "2024 ATA908-117 frame on consumption report shows 28186pcs …
                12398 Olefin FG on JV consumption report was deducted from
                28186 … = 15788"
              </i>
              .
            </p>
            <p style={{ margin: "4px 0" }}>
              This app reports the raw consumption, not the deducted figure —
              the plan removes JV-produced Olefin FG so those frames aren&rsquo;t
              counted twice against the Olefin tab. Neither is wrong; they
              answer different questions. Expect the same on other high-volume
              ATA rows.
            </p>
            <p style={{ margin: "4px 0 0" }}>
              One detail worth knowing: this reads <b>28,675</b>, not the
              28,186 quoted above. The difference is exactly 489 units in
              location 22, Type&nbsp;B. That note quotes a
              location-15-only report, while the workbook&rsquo;s CFMT rows on
              the Base tab include Type&nbsp;B and match this app to the unit
              only when it is counted. So the workbook is not consistent about
              location scope between tabs — this app counts Jurupa, March
              Products Texas and Type&nbsp;B everywhere, and excludes the Gold
              Crest JV.
            </p>
          </Note>
          <Note title="ATAF908-117 and ATA908-117 are interchangeable in NetSuite">
            The plan may combine them; this table keeps them separate because
            NetSuite records them separately. Compare the <b>sum</b> of the two
            rows, not either alone.
          </Note>
          <Note title="Known open items">
            <b>LUNA774E-DT-174</b> and <b>LUNA906B-DT-174</b> run ~10% below
            the plan consistently (448 vs 488). <b>STAF908-202</b> and{" "}
            <b>STAF118-202</b> run above it in FY2026 only. Neither matches the
            JV-deduction pattern, so both are unexplained and worth a look.
          </Note>
        </>
      )}

      {category === "Olefin" && (
        <>
          <Note title="★ SWV / DWV — why 11ft covers look empty before FY2026">
            <p style={{ margin: "4px 0" }}>
              <b>SWV</b> (single wind vent) was implied for 908 (9ft) and
              smaller covers; <b>DWV</b> was the default for 118 (11ft). 118
              only switched to default-SWV last year.
            </p>
            <p style={{ margin: "4px 0" }}>
              So the historical 11ft demand sits under <code>-DWV</code>, while
              the plan reports it under the current <code>-SWV</code> name.
              Verified: <code>C118-F03-DWV</code> = 551 in FY2024, exactly the
              plan's SWV row; FY2025 = DWV&nbsp;347 + SWV&nbsp;1 = 348, again
              exact.
            </p>
            <p style={{ margin: "4px 0 0" }}>
              This table keeps <b>DWV and SWV as separate SKUs</b>, as NetSuite
              does. To compare against a plan row, <b>add the two together</b>.
            </p>
          </Note>
          <Note title="★ C908 covers read high — the same JV deduction">
            In FY2024 the C908 olefin covers total <b>+12,466</b> over the
            plan — within 1% of the <b>12,398</b> "Olefin FG on JV consumption
            report" deduction documented on the workbook's ATA&nbsp;FG tab. The
            app reports raw consumption; the plan nets out JV production.
          </Note>
          <Note tone="blue" defaultOpen title="Where the two agree exactly">
            All six <b>C758</b> (7.5ft) covers and every <b>C118&nbsp;-DWV</b>{" "}
            match the plan to the unit (54=54, 82=82, 89=89). That's the
            evidence the differences above are reporting choices, not
            attribution errors.
          </Note>
          <Note title="Not tracked: bespoke logo covers">
            One-off customer covers (<code>*-LOGO-*</code> and similar) carry
            real demand but are intentionally excluded — they're custom jobs,
            absent from the plan, and would clutter the catalog.
          </Note>
        </>
      )}

      {category === "Pacifica" && (
        <>
          <Note tone="green" title="★ Every row matches the plan exactly">
            All 42 Pacifica colours agree with the 2027 plan on FY2024, FY2025
            and FY2026 — not close, identical. The 28 C908 and C758 rows match
            outright; the 14 C118 rows match once the SWV and DWV twins are
            added together, as described below. This is the only tab that
            reconciles completely, which makes it the reference point when
            another tab disagrees: the method is sound, so a gap elsewhere is
            scope or naming, not a bad pull.
          </Note>
          <Note title="★ 11ft is split SWV / DWV — sum the pair to compare">
            The plan carries one row per 11ft colour, named <code>-SWV</code>.
            NetSuite carries two items, <code>-SWV</code> and <code>-DWV</code>,
            and before the 11ft default changed to single wind vent almost all
            of it was DWV. So a plan row of 121 appears here as 13 SWV plus a
            separate DWV row — the demand is not missing, it is on the twin.
            Summed, all 14 match the plan exactly in every year. Both are
            listed, DWV directly beneath its SWV sibling; add the pair to
            compare against a plan line.
          </Note>
          <Note title="The six colours the plan flags for cut are still selling">
            SA21, SA32, SA36, SA40, SA52 and SA65 carry the plan note
            &ldquo;potentially decrease 20 SA colors to 14&rdquo;. They are not
            dormant: 758 units across FY2026, led by C908-SA36 (111), C758-SA36
            (92) and C758-SA52 (89). Worth reading as a live decision rather
            than a formality.
          </Note>
          <Note title="Known open items">
            <ul className="list-disc space-y-1 pl-4">
              <li>
                Bespoke logo covers are excluded, as on Olefin — e.g.
                C758-SA52-JERSEY&nbsp;MIKES-LOGO (183 units FY2026) is real
                demand that no plan row covers.
              </li>
              <li>
                The three <code>C852PD-SA…/SA…</code> combination rows in the
                plan have no single matching NetSuite item and are not tracked
                yet.
              </li>
            </ul>
          </Note>
        </>
      )}


      {category === "Sunbrella" && (
        <>
          <Note
            tone="amber"
            defaultOpen
            title="★ Compare carried colours only — raw size totals are not comparable"
          >
            <p style={{ margin: "0 0 6px" }}>
              These rows come from a size &times; fabric-code grid, and that grid
              assumes every size is offered in every colour. It is not.{" "}
              <strong>C604 and C608 carry 10 colours</strong> (5401, 5403, 5404,
              5408, 5417, 5422, 5429, 5439, 5446, 5457); C908, C758 and C118
              carry 48. The grid produces 275 keys where only 164 are real
              product, inventing ~45 rows per short size.
            </p>
            <p style={{ margin: 0 }}>
              So the table defaults to <strong>Carried colours only</strong>.
              Restricted that way C608 matches the plan exactly in all three
              years — 970 / 784 / 820 — the same standard Pacifica met. A size
              total taken across all grid rows is not a like-for-like
              comparison.
            </p>
          </Note>
          <Note tone="blue" defaultOpen title="Non-carried colours are held back, not dropped">
            <p style={{ margin: "0 0 6px" }}>
              Real demand does land on combinations March does not carry — 306
              units on C604 in FY2026. It is genuine special-order work, not an
              attribution error: <code>C604-5453</code> has its own invoices
              across nine orders in six months, plus a dozen FG parents that
              consume it.
            </p>
            <p style={{ margin: 0 }}>
              Those keys are therefore still tracked rather than deleted —
              discarding them would lose real demand silently. They sit outside
              the default view, with a line above the table saying how many rows
              and units are held back; untick the checkbox to include them. Do
              not plan against them: they are special orders, not carried
              product.
            </p>
          </Note>
          <Note title="★ One row here answers a SWV/DWV pair in the plan">
            These keys collapse the wind vent: <code>C908-5401</code> covers
            both <code>C908-5401-SWV</code> and <code>-DWV</code>. The plan
            lists them separately (10 codes have both), so add the plan&rsquo;s
            pair before comparing — the same arithmetic as the 11ft rows on
            Olefin and Pacifica.
          </Note>
          <Note title="Demand only — no supply, by design">
            These are synthetic aggregation keys, not orderable items: no
            inventory, no PO, no lead time. They never appear on To order or
            Arriving, and Forecast leaves shortfall and
            suggested-order blank for them. This tab answers &ldquo;how much was
            consumed&rdquo;, never &ldquo;how much to order&rdquo;.
          </Note>
          <Note defaultOpen title="Known open items">
            <ul className="list-disc space-y-1 pl-4">
              <li>
                <strong>Two things were being counted that the plan excludes.</strong>{" "}
                Bespoke customer covers were absorbed into these base patterns
                — a logo cover shares the prefix without being the same product
                — and so were the <code>-LP</code> / <code>-LH</code>
                construction variants. Removing both took the gap against the
                plan from +6-24% down to about +1-2%:
              </li>
              <li>
                <strong>C604 and C608 now match the plan exactly.</strong> C604
                is 594 against 594 in FY2026 and 634 against 633 in FY2025 —
                the size that started at +24%. C608 is exact in all three years
                (970 / 784 / 820). C908, C758 and C118 sit at +1-2%.
              </li>
              <li>
                The <code>-LP</code>/<code>-LH</code> exclusion was tested
                rather than assumed: if those variants were legitimate product,
                removing them would have pushed some size below the plan.{" "}
                <strong>None of the 15 size-years went below.</strong> C608 was
                the control — it has no -LP/-LH at all, so it had to stay exact,
                and it did.
              </li>
              <li>
                What is left is roughly <strong>+1-2%</strong>, still
                one-directional (the dashboard is at or above the plan, never
                below), which is the signature of a further small deduction on
                the plan side rather than a counting error. The only outlier is{" "}
                <strong>C118 in FY2025, +6%</strong> — down from +19%, and now a
                single size-year rather than a pattern.
              </li>
              <li>
                <strong>
                  <code>C1188</code>, <code>C852PD</code> and{" "}
                  <code>C852CLAS</code> are missing entirely.
                </strong>{" "}
                They are carried and on the plan&rsquo;s tab — roughly 5,300
                units in FY2024, ~13% of its Sunbrella total, and C852PD alone
                has ~2,000 units of direct FY2026 demand — but no key here
                represents them. Their SKUs do not follow the size-code shape:
                names like <code>C852PD-14051-0054-57003-AL-BND503016</code>{" "}
                carry binding and colour segments the grid cannot express.
                Closing this needs a real SKU list, not a wider grid.
              </li>
            </ul>
          </Note>
        </>
      )}


      {(category === "Trim" || category === "ProtectiveCover") && (
        <Note tone="blue" defaultOpen title="Split out of Base on 19 Oct 2026">
          The planning workbook keeps {category === "Trim" ? "binding, fringe and tassels" : "protective covers"} on its
          Base&amp;accessories tab. The app now tracks them as their own category, so compare this tab with those rows of
          the workbook's Base tab. The notes on the Base tab about how these items are counted still apply.
          {category === "Trim" && " Quantities are in yards."}
        </Note>
      )}

      {category === "Base" && (
        <>
          <Note tone="green" defaultOpen title="★ 42 of 55 rows match the plan exactly">
            Every PC accessory matches to the unit, and so do UC101, DISD912,
            PT-CRL-12-BT and the CRL / RL / TB poles. BDNATURAL — the largest
            row on the tab — comes to 101,076 against a plan of 101,366. A
            further 7 rows sit within 5%.
          </Note>
          <Note tone="blue" defaultOpen title="Binding and trim are reached through the BOM, not sold">
            BD* binding and FT-* trim are never sold on their own. They are
            counted by expanding invoiced umbrella sales down through the BOM
            (umbrella &rarr; cover &rarr; binding) at the real BOM quantity —
            around 16 yards of binding per cover. That expansion turns out to
            reach essentially all of it: FT-FRN11-SA04 0.98 of plan,
            FT-TAS11-SA04 1.01, FT-FRN11-SA39 1.01, BDNAVY 1.02.
          </Note>
          <Note title="★ CFMT bases read ~7% low, and it is the location filter">
            <p style={{ margin: "0 0 6px" }}>
              CFMT160-BLACK reads 3,806 against a plan of 4,070, and
              CFMT160-WHITE 3,526 against 3,828. Both gaps are exactly the
              locations this app does not pull:
            </p>
            <p style={{ margin: "0 0 6px", fontFamily: "monospace", fontSize: 12 }}>
              CFMT160-BLACK &nbsp;all locations = 4,070 = plan<br />
              CFMT160-WHITE &nbsp;all locations = 3,828 = plan
            </p>
            <p style={{ margin: 0 }}>
              Demand here is scoped to locations 15 and 64; the plan counts
              every location. For directly-sold items that is a systematic
              under-count of a few percent. It is not a defect in the
              expansion, and widening the filter would move every other tab
              too, so this is a scoping decision rather than a bug to fix
              quietly.
            </p>
          </Note>
          <Note title="Known open items">
            <ul className="list-disc space-y-1 pl-4">
              <li>
                <strong>BDBLACK is 2,834 against 3,038 (0.93)</strong> and is
                the one row not explained by location scope. It has no direct
                sales at all, so it depends entirely on BOM expansion — which
                is also why the 2027 plan handoff could not settle it from
                invoices either.
              </li>
              <li>
                FRINGE, FRINGE-BEIGE/BLUE/YELLOW, UC100, UC102 and
                FT-TAS11-SA08/SA61 have neither a plan figure nor any demand.
                They are tracked so they surface if they wake up.
              </li>
              <li>
                PC* are the newer accessory codes and CRL/RL/TB the older ones
                they replaced. The handoff notes the plan may still route some
                demand through the old codes, so read the two families together
                before drawing conclusions from a single row.
              </li>
            </ul>
          </Note>
        </>
      )}

    </div>
  );
}
